"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import axios from "axios";
import { ArrowRight, Check, Plus } from "lucide-react";
import { Button } from "../Button";
import { fetchPriceList, summarise, type Unit } from "@/lib/v2/availability";
import { formatPriceShort, formatPsf, sanePrice, statusMeta, tenureLabel, topYear } from "@/lib/v2/format";
import { cn, formatPropertyName } from "@/lib/utils";
import { Pill } from "../Pill";
import type { NearbyProject, ProjectProperty } from "./types";

type Column = {
  id: string;
  name: string;
  href?: string;
  district?: string;
  distanceKm?: number | null;
  status: ReturnType<typeof statusMeta>;
  tenure: string;
  top: string | null;
  units: number | null;
  priceFrom: number | null;
  psfFrom: number | null;
  unitsData: Unit[] | undefined;
  loading: boolean;
};

const MAX_PICKS = 3;
const API = process.env.NEXT_PUBLIC_API_URL ?? "";

// The nearby list carries only a few fields, so each picked project is
// topped up from its full record (TOP, units, tenure, psf, coordinates).
async function fetchFullRecord(name: string): Promise<Partial<NearbyProject>> {
  try {
    const res = await axios.get(`${API}/property/get-property-by-name/${formatPropertyName(name)}`);
    const d = res.data?.data ?? res.data;
    return d && typeof d === "object" ? (d as Partial<NearbyProject>) : {};
  } catch {
    return {};
  }
}

function km(aLat?: number | null, aLng?: number | null, bLat?: number | null, bLng?: number | null): number | null {
  if (![aLat, aLng, bLat, bLng].every((v) => typeof v === "number" && Number.isFinite(v))) return null;
  const r = 6371;
  const dLat = ((bLat! - aLat!) * Math.PI) / 180;
  const dLng = ((bLng! - aLng!) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos((aLat! * Math.PI) / 180) * Math.cos((bLat! * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
}
const BEDROOMS = ["1 Bedroom", "2 Bedroom", "3 Bedroom", "4 Bedroom"];

function bedroomKey(type: string) {
  const m = type.match(/^(\d)\s*Bedroom/i);
  return m ? `${m[1]} Bedroom` : null;
}

/**
 * Side-by-side table: this project against up to three nearby launches.
 * Availability rows come from each project's live unit list.
 */
export function ProjectCompare({ property, nearby, currentUnits }: { property: ProjectProperty; nearby: NearbyProject[]; currentUnits: Unit[] }) {
  const candidates = useMemo(() => nearby.filter((n) => n._id !== property._id).slice(0, 8), [nearby, property._id]);
  const [picked, setPicked] = useState<string[]>([]);
  useEffect(() => {
    setPicked(candidates.slice(0, 2).map((c) => c._id));
  }, [candidates]);

  const pickedRaw = candidates.filter((c) => picked.includes(c._id));
  const records = useQueries({
    queries: pickedRaw.map((p) => ({
      queryKey: ["v2-compare-record", p._id],
      queryFn: () => fetchFullRecord(p.name),
      staleTime: 10 * 60_000,
    })),
  });
  const pickedProjects: NearbyProject[] = pickedRaw.map((p, i) => ({ ...p, ...(records[i]?.data ?? {}), _id: p._id, name: p.name }));
  const queries = useQueries({
    queries: pickedProjects.map((p) => ({
      queryKey: ["v2-compare-units", p._id],
      queryFn: () => fetchPriceList(p._id),
      staleTime: 5 * 60_000,
    })),
  });

  const columns: Column[] = [
    {
      id: property._id,
      name: property.title || property.name,
      district: property.district,
      status: statusMeta(property.construction_status, property.isPreLaunch),
      tenure: tenureLabel(property.is_leasehold, property.lease_year),
      top: topYear(property.expected_top),
      units: property.units || null,
      priceFrom: sanePrice(property.min_flat_price),
      psfFrom: property.psf_from ?? null,
      unitsData: currentUnits,
      loading: false,
    },
    ...pickedProjects.map((p, i) => ({
      id: p._id,
      name: p.title || p.name,
      href: `/single-property/${formatPropertyName(p.name)}`,
      district: p.district,
      distanceKm:
        p.distance_km ??
        (typeof p.distance === "number" ? p.distance : null) ??
        km(property.location?.latitude, property.location?.longitude, p.location?.latitude, p.location?.longitude),
      status: statusMeta(p.construction_status || "Upcoming", p.isPreLaunch),
      tenure: tenureLabel(Boolean(p.is_leasehold), p.lease_year),
      top: topYear(p.expected_top),
      units: p.units || null,
      priceFrom: sanePrice(p.min_flat_price),
      psfFrom: p.psf_from ?? null,
      unitsData: queries[i]?.data,
      loading: queries[i]?.isLoading ?? false,
    })),
  ];

  const summaries = columns.map((c) => (c.unitsData ? summarise(c.unitsData) : null));
  const toggle = (id: string) =>
    setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= MAX_PICKS ? cur : [...cur, id]));

  const cell = (key: string, value: React.ReactNode, muted = false) => (
    <td key={key} className={cn("px-4 py-3 text-sm", muted ? "text-ink-faint" : "text-ink")}>{value ?? <span className="text-ink-faint">Not listed</span>}</td>
  );

  const rows: Array<{ label: string; render: (c: Column, i: number) => React.ReactNode }> = [
    { label: "Stage", render: (c) => <Pill tone={c.status.tone}>{c.status.label}</Pill> },
    { label: "District", render: (c) => c.district || null },
    { label: "Distance", render: (c, i) => (i === 0 ? "This project" : c.distanceKm != null ? `${c.distanceKm.toFixed(1)} km away` : null) },
    { label: "Tenure", render: (c) => c.tenure },
    { label: "Expected TOP", render: (c) => c.top },
    { label: "Total units", render: (c) => (c.units != null ? c.units.toLocaleString("en-SG") : null) },
    {
      label: "Units available",
      render: (c, i) => {
        const s = summaries[i];
        if (c.loading) return <span className="inline-block h-4 w-20 animate-pulse rounded bg-surface" />;
        if (!s || s.total === 0) return c.status.tone === "soldout" ? "Sold out" : "Not released";
        return (
          <span className="font-semibold text-avail">
            {s.available} of {s.total}
          </span>
        );
      },
    },
    {
      label: "From",
      render: (c, i) => {
        const s = summaries[i];
        const v = (s && s.minPrice) || c.priceFrom;
        return v ? <span className="font-semibold text-gold-600">{formatPriceShort(v)}</span> : null;
      },
    },
    {
      label: "PSF from",
      render: (c, i) => {
        const s = summaries[i];
        const v = (s && s.minPsf) || c.psfFrom;
        return v ? formatPsf(v) : null;
      },
    },
    ...BEDROOMS.map((bed) => ({
      label: `${bed} from`,
      render: (_c: Column, i: number) => {
        const s = summaries[i];
        if (!s) return null;
        const rows = s.byType.filter((t) => bedroomKey(t.type) === bed);
        const avail = rows.reduce((n, r) => n + r.available, 0);
        const prices = rows.map((r) => r.minPrice).filter((p): p is number => p != null && p > 0);
        if (rows.length === 0) return null;
        if (avail === 0) return <span className="text-ink-faint">Sold out</span>;
        return (
          <span>
            {prices.length ? formatPriceShort(Math.min(...prices)) : "Price on request"}
            <span className="ml-1 text-xs text-ink-muted">· {avail} left</span>
          </span>
        );
      },
    })),
  ];

  return (
    <div>
      {candidates.length > 0 && (
        <div className="mb-5">
          <p className="text-sm text-ink-muted">Pick up to three nearby launches to compare.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {candidates.map((c) => {
              const on = picked.includes(c._id);
              const disabled = !on && picked.length >= MAX_PICKS;
              return (
                <button
                  key={c._id}
                  type="button"
                  onClick={() => toggle(c._id)}
                  disabled={disabled}
                  aria-pressed={on}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-40",
                    on ? "border-ink bg-ink text-white" : "border-line bg-white text-ink hover:bg-surface",
                  )}
                >
                  {on ? <Check aria-hidden="true" className="size-4" /> : <Plus aria-hidden="true" className="size-4" />}
                  {c.title || c.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-line bg-white">
        <table className="w-full min-w-[720px] border-collapse text-left">
          <thead>
            <tr className="border-b border-line bg-surface">
              <th scope="col" className="w-44 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-ink-faint" />
              {columns.map((c, i) => (
                <th key={c.id} scope="col" className="px-4 py-3 align-top">
                  {c.href ? (
                    <Link href={c.href} className="font-display text-base font-semibold text-ink hover:text-brand-700">
                      {c.name}
                    </Link>
                  ) : (
                    <span className="font-display text-base font-semibold text-brand-700">{c.name}</span>
                  )}
                  {i === 0 && <span className="mt-1 block text-[11px] font-medium uppercase tracking-wide text-ink-faint">You are viewing</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-line last:border-b-0">
                <th scope="row" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-faint">
                  {r.label}
                </th>
                {columns.map((c, i) => cell(`${r.label}-${c.id}`, r.render(c, i)))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-brand-500/30 bg-brand-50 px-5 py-5 md:flex-row md:items-center md:justify-between md:px-7">
        <div>
          <h3 className="font-display text-lg font-semibold text-ink">Compare the units in detail</h3>
          <p className="mt-1 text-sm text-ink-muted">
            The full tool shows every bedroom type side by side: price and PSF ranges, sizes, units left and floor plans. Add any project in Singapore, not only the ones nearby.
          </p>
        </div>
        <Button
          href={`/compare?projects=${[property.name, ...pickedProjects.map((p) => p.name)].map(formatPropertyName).join(",")}`}
          className="shrink-0"
        >
          Open the full compare tool
          <ArrowRight aria-hidden="true" className="size-4" />
        </Button>
      </div>
    </div>
  );
}
